import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useSocialMetrics } from '../lib/hooks/useSocialMetrics';
import { 
  BarChart2, 
  TrendingUp, 
  Users, 
  Eye, 
  ArrowUp, 
  ArrowDown,
  Twitter,
  Facebook,
  Instagram,
  Youtube,
  Linkedin,
  Sparkles,
  CheckCircle2,
  AlertTriangle,
  Lightbulb,
  Save,
  Move,
  Settings
} from 'lucide-react';
import { supabase } from '../lib/supabase';

interface Platform {
  id: string;
  name: string;
  icon: typeof Twitter;
  color: string;
  enabled: boolean;
  metrics: {
    views: number;
    engagement: number;
    followers: number;
    contentScore: number;
  };
}

interface PlatformLayout {
  id: string;
  position: number;
}

const Performance = () => {
  // Placeholder platform data
  const [platforms, setPlatforms] = useState<Platform[]>([
    {
      id: 'twitter',
      name: 'Twitter',
      icon: Twitter,
      color: '#1DA1F2',
      enabled: true,
      metrics: {
        views: 45000,
        engagement: 4.3,
        followers: 15200,
        contentScore: 85,
      },
    },
    {
      id: 'facebook',
      name: 'Facebook',
      icon: Facebook,
      color: '#1877F2',
      enabled: true,
      metrics: {
        views: 32500,
        engagement: 3.8,
        followers: 12800,
        contentScore: 78,
      },
    },
    {
      id: 'instagram',
      name: 'Instagram',
      icon: Instagram,
      color: '#E4405F',
      enabled: true,
      metrics: {
        views: 28000,
        engagement: 5.2,
        followers: 9800,
        contentScore: 92,
      },
    },
    {
      id: 'youtube',
      name: 'YouTube',
      icon: Youtube,
      color: '#FF0000',
      enabled: true,
      metrics: {
        views: 15000,
        engagement: 2.8,
        followers: 5200,
        contentScore: 71,
      },
    },
    {
      id: 'linkedin',
      name: 'LinkedIn',
      icon: Linkedin,
      color: '#0A66C2',
      enabled: true,
      metrics: {
        views: 8000,
        engagement: 3.1,
        followers: 2200,
        contentScore: 88,
      },
    },
  ]);
  const [platformLayouts, setPlatformLayouts] = useState<PlatformLayout[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [draggedPlatform, setDraggedPlatform] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const { metrics, loading: metricsLoading } = useSocialMetrics();

  // Load saved layout on component mount
  React.useEffect(() => {
    loadPlatformLayout();
  }, []);

  // Load platform layout from user's account
  const loadPlatformLayout = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('platform_layouts')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle(); // Use maybeSingle() to handle no rows gracefully

      if (error) {
        console.error('Error loading platform layout:', error);
        return;
      }

      if (data?.layout) {
        setPlatformLayouts(data.layout);
      } else {
        // Create default layout if none exists
        const defaultLayout = platforms.map((platform, index) => ({
          id: platform.id,
          position: index
        }));
        setPlatformLayouts(defaultLayout);
      }
    } catch (err) {
      console.error('Error loading platform layout:', err);
    }
  };

  // Save platform layout to user's account
  const savePlatformLayout = async () => {
    try {
      setIsSaving(true);
      setSaveSuccess(false);

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const { error } = await supabase
        .from('platform_layouts')
        .upsert({
          user_id: user.id,
          layout: platformLayouts,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'user_id'
        });

      if (error) throw error;
      setSaveSuccess(true);

      // Hide success message after 3 seconds
      setTimeout(() => {
        setSaveSuccess(false);
      }, 3000);
    } catch (err) {
      console.error('Error saving platform layout:', err);
    } finally {
      setIsSaving(false);
    }
  };

  // Calculate aggregated metrics based on enabled platforms
  const aggregatedMetrics = platforms
    .filter(p => p.enabled)
    .reduce(
      (acc, platform) => ({
        views: acc.views + (metrics.platforms[platform.id]?.views || 0),
        engagement: acc.engagement + (metrics.platforms[platform.id]?.engagement || 0),
        followers: acc.followers + (metrics.platforms[platform.id]?.followers || 0),
        contentScore: acc.contentScore + (metrics.platforms[platform.id]?.engagementRate || 0),
      }),
      { views: 0, engagement: 0, followers: 0, contentScore: 0 }
    );

  // Calculate average content score
  const averageContentScore = 
    platforms.filter(p => p.enabled).length > 0
      ? aggregatedMetrics.contentScore / platforms.filter(p => p.enabled).length
      : 0;

  const togglePlatform = (platformId: string) => {
    setPlatforms(prev =>
      prev.map(platform =>
        platform.id === platformId
          ? { ...platform, enabled: !platform.enabled }
          : platform
      )
    );
  };

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'text-green-500 dark:text-green-400';
    if (score >= 60) return 'text-yellow-500 dark:text-yellow-400';
    return 'text-red-500 dark:text-red-400';
  };

  const getScoreLabel = (score: number) => {
    if (score >= 80) return 'Excellent';
    if (score >= 60) return 'Average';
    return 'Needs Improvement';
  };

  // Handle drag start
  const handleDragStart = (platformId: string) => {
    setIsDragging(true);
    setDraggedPlatform(platformId);
  };

  // Handle drag end
  const handleDragEnd = () => {
    setIsDragging(false);
    setDraggedPlatform(null);
  };

  // Handle platform drop
  const handleDrop = (targetPlatformId: string) => {
    if (!draggedPlatform || draggedPlatform === targetPlatformId) return;

    const draggedLayout = platformLayouts.find(p => p.id === draggedPlatform);
    const targetLayout = platformLayouts.find(p => p.id === targetPlatformId);

    if (!draggedLayout || !targetLayout) return;

    // Swap positions
    const newLayouts = platformLayouts.map(layout => {
      if (layout.id === draggedPlatform) {
        return { ...layout, position: targetLayout.position };
      }
      if (layout.id === targetPlatformId) {
        return { ...layout, position: draggedLayout.position };
      }
      return layout;
    });

    setPlatformLayouts(newLayouts);
  };

  // Get sorted platforms based on layout
  const getSortedPlatforms = () => {
    if (platformLayouts.length === 0) return platforms;

    return [...platforms].sort((a, b) => {
      const aLayout = platformLayouts.find(p => p.id === a.id);
      const bLayout = platformLayouts.find(p => p.id === b.id);
      
      if (!aLayout) return 1;
      if (!bLayout) return -1;
      
      return aLayout.position - bLayout.position;
    });
  };

  const sortedPlatforms = getSortedPlatforms();

  // Placeholder AI recommendations based on content score
  const getAiRecommendations = (score: number) => {
    if (score >= 80) {
      return [
        "Continue posting consistently to maintain high engagement",
        "Experiment with new content formats to diversify your reach",
        "Consider cross-platform content promotion for wider visibility"
      ];
    } else if (score >= 60) {
      return [
        "Increase posting frequency during peak engagement hours",
        "Add more visual content to improve engagement rates",
        "Engage more with your audience through comments and replies"
      ];
    } else {
      return [
        "Develop a consistent posting schedule",
        "Include more relevant hashtags to increase visibility",
        "Create more engaging content with calls-to-action"
      ];
    }
  };

  // Get platform-specific recommendations
  const getPlatformRecommendations = (platform: Platform) => {
    const recommendations: Record<string, string[]> = {
      twitter: [
        "Use trending hashtags to increase visibility",
        "Engage in relevant Twitter conversations",
        "Post during high-activity hours (9 AM - 3 PM)"
      ],
      facebook: [
        "Share more video content for higher engagement",
        "Create engaging polls and questions",
        "Utilize Facebook Stories for increased reach"
      ],
      instagram: [
        "Post consistently to Instagram Stories",
        "Use relevant hashtags (up to 30)",
        "Create more Reels for better reach"
      ],
      youtube: [
        "Optimize video titles and thumbnails",
        "Create longer content (8-12 minutes)",
        "Add end screens and cards to videos"
      ],
      linkedin: [
        "Share industry insights and expertise",
        "Post during business hours",
        "Engage with industry leaders' content"
      ]
    };

    return recommendations[platform.id] || [];
  };

  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-8 overflow-x-hidden w-full"
    >
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">Performance Analytics</h1>
        <p className="mt-1 sm:mt-2 text-sm sm:text-base text-gray-600 dark:text-gray-400">Track your content performance and engagement metrics</p>
      </div>

      {/* Platform Toggles */}
      <div className="bg-white dark:bg-dark-800 rounded-lg shadow-sm p-4 sm:p-6">
        <h2 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white mb-3 sm:mb-4">Platforms</h2>
        <div className="flex flex-wrap gap-2 sm:gap-4">
          {platforms.map(platform => {
            const Icon = platform.icon;
            return (
              <motion.button
                key={platform.id}
                onClick={() => togglePlatform(platform.id)}
                className={`flex items-center space-x-1 sm:space-x-2 px-2 sm:px-4 py-1.5 sm:py-2 rounded-lg transition-all ${
                  platform.enabled
                    ? 'bg-gray-100 dark:bg-dark-700'
                    : 'bg-gray-50 dark:bg-dark-800 opacity-50'
                }`}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <Icon
                  className="h-4 w-4 sm:h-5 sm:w-5"
                  style={{ color: platform.enabled ? platform.color : '#9CA3AF' }}
                />
                <span className={`text-xs sm:text-sm font-medium ${
                  platform.enabled
                    ? 'text-gray-900 dark:text-white'
                    : 'text-gray-500 dark:text-gray-400'
                }`}>
                  {platform.name}
                </span>
              </motion.button>
            );
          })}
        </div>
      </div>

      {/* Aggregated Metrics */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6 w-full">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white dark:bg-dark-800 rounded-lg p-4 sm:p-6 shadow-sm"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400">Total Views</p>
              <p className="mt-1 sm:mt-2 text-xl sm:text-3xl font-semibold text-gray-900 dark:text-white">
                {aggregatedMetrics.views.toLocaleString()}
              </p>
            </div>
            <div className="p-2 sm:p-3 bg-gray-50 dark:bg-dark-700 rounded-lg">
              <Eye className="h-5 w-5 sm:h-6 sm:w-6 text-gray-600 dark:text-gray-400" />
            </div>
          </div>
          <div className="mt-2 sm:mt-4 flex items-center">
            <ArrowUp className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-green-500" />
            <span className="ml-1 sm:ml-2 text-xs sm:text-sm font-medium text-green-500">+12.3%</span>
            <span className="ml-1 sm:ml-2 text-xs sm:text-sm text-gray-500">vs last month</span>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white dark:bg-dark-800 rounded-lg p-4 sm:p-6 shadow-sm"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400">Avg. Engagement</p>
              <p className="mt-1 sm:mt-2 text-xl sm:text-3xl font-semibold text-gray-900 dark:text-white">
                {(aggregatedMetrics.engagement / platforms.filter(p => p.enabled).length).toFixed(1)}%
              </p>
            </div>
            <div className="p-2 sm:p-3 bg-gray-50 dark:bg-dark-700 rounded-lg">
              <TrendingUp className="h-5 w-5 sm:h-6 sm:w-6 text-gray-600 dark:text-gray-400" />
            </div>
          </div>
          <div className="mt-2 sm:mt-4 flex items-center">
            <ArrowUp className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-green-500" />
            <span className="ml-1 sm:ml-2 text-xs sm:text-sm font-medium text-green-500">+8.1%</span>
            <span className="ml-1 sm:ml-2 text-xs sm:text-sm text-gray-500">vs last month</span>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white dark:bg-dark-800 rounded-lg p-4 sm:p-6 shadow-sm"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400">Total Followers</p>
              <p className="mt-1 sm:mt-2 text-xl sm:text-3xl font-semibold text-gray-900 dark:text-white">
                {aggregatedMetrics.followers.toLocaleString()}
              </p>
            </div>
            <div className="p-2 sm:p-3 bg-gray-50 dark:bg-dark-700 rounded-lg">
              <Users className="h-5 w-5 sm:h-6 sm:w-6 text-gray-600 dark:text-gray-400" />
            </div>
          </div>
          <div className="mt-2 sm:mt-4 flex items-center">
            <ArrowDown className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-red-500" />
            <span className="ml-1 sm:ml-2 text-xs sm:text-sm font-medium text-red-500">-2.4%</span>
            <span className="ml-1 sm:ml-2 text-xs sm:text-sm text-gray-500">vs last month</span>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white dark:bg-dark-800 rounded-lg p-4 sm:p-6 shadow-sm"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400">Content Score</p>
              <p className="mt-1 sm:mt-2 text-xl sm:text-3xl font-semibold text-gray-900 dark:text-white">
                {averageContentScore.toFixed(0)}
              </p>
            </div>
            <div className="p-2 sm:p-3 bg-gray-50 dark:bg-dark-700 rounded-lg">
              <BarChart2 className="h-5 w-5 sm:h-6 sm:w-6 text-gray-600 dark:text-gray-400" />
            </div>
          </div>
          <div className="mt-2 sm:mt-4 flex items-center">
            <span className={`text-xs sm:text-sm font-medium ${getScoreColor(averageContentScore)}`}>
              {getScoreLabel(averageContentScore)}
            </span>
          </div>
        </motion.div>
      </div>

      {/* Content Score Meter */}
      <div className="bg-white dark:bg-dark-800 rounded-lg shadow-sm p-6 w-full">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">Content Score Meter</h2>
        <div className="relative h-48">
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="relative w-48 h-48">
              {/* Meter Background */}
              <svg className="w-full h-full transform -rotate-90">
                <circle
                  cx="96"
                  cy="96"
                  r="88"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="16"
                  className="text-gray-100 dark:text-dark-700"
                />
                {/* Score Indicator */}
                <circle
                  cx="96"
                  cy="96"
                  r="88"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="16"
                  strokeDasharray={`${(averageContentScore / 100) * 553} 553`}
                  className={`${getScoreColor(averageContentScore)}`}
                  style={{ transition: 'stroke-dasharray 0.5s ease-in-out' }}
                />
              </svg>
              {/* Score Display */}
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="text-4xl font-bold text-gray-900 dark:text-white">
                  {averageContentScore.toFixed(0)}
                </span>
                <span className={`text-sm font-medium ${getScoreColor(averageContentScore)}`}>
                  {getScoreLabel(averageContentScore)}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Platform Performance Section with Draggable Blocks */}
      <div className="space-y-4 w-full">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
            <BarChart2 className="h-5 w-5 mr-2 text-indigo-600 dark:text-indigo-400" />
            Platform Performance
          </h2>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={savePlatformLayout}
            disabled={isSaving}
            className={`flex items-center space-x-2 px-3 py-1.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-sm ${
              isSaving ? 'opacity-70 cursor-not-allowed' : ''
            }`}
          >
            {isSaving ? (
              <>
                <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2" />
                <span>Saving...</span>
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-1" />
                <span>Save Layout</span>
              </>
            )}
          </motion.button>
        </div>
        
        {saveSuccess && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3 text-sm text-green-700 dark:text-green-300 flex items-center"
          >
            <CheckCircle2 className="h-4 w-4 mr-2" />
            Layout saved successfully!
          </motion.div>
        )}
        
        <div className="text-sm text-gray-500 dark:text-gray-400 mb-4 flex items-center">
          <Move className="h-4 w-4 mr-2" />
          Drag and drop platform blocks to rearrange them
        </div>
        
        {/* Platform Grid - 2 on top, 3 on bottom */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4 w-full">
          {sortedPlatforms.slice(0, 2).filter(p => p.enabled).map(platform => (
            <PlatformBlock
              key={platform.id}
              platform={platform}
              isDragging={isDragging}
              isBeingDragged={draggedPlatform === platform.id}
              onDragStart={() => handleDragStart(platform.id)}
              onDragEnd={handleDragEnd}
              onDrop={() => handleDrop(platform.id)}
              getScoreColor={getScoreColor}
            />
          ))}
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 w-full">
          {sortedPlatforms.slice(2, 5).filter(p => p.enabled).map(platform => (
            <PlatformBlock
              key={platform.id}
              platform={platform}
              isDragging={isDragging}
              isBeingDragged={draggedPlatform === platform.id}
              onDragStart={() => handleDragStart(platform.id)}
              onDragEnd={handleDragEnd}
              onDrop={() => handleDrop(platform.id)}
              getScoreColor={getScoreColor}
            />
          ))}
        </div>
      </div>

      {/* AI Recommendations */}
      <div className="bg-white dark:bg-dark-800 rounded-lg shadow-sm p-6 w-full">
        <div className="flex items-center space-x-2 mb-6">
          <Sparkles className="h-6 w-6 text-indigo-600 dark:text-indigo-400" />
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">AI-Powered Recommendations</h2>
        </div>

        {/* Overall Score Recommendations */}
        <div className="mb-8">
          <div className="flex items-center space-x-2 mb-4">
            <Lightbulb className="h-5 w-5 text-amber-500" />
            <h3 className="text-md font-medium text-gray-900 dark:text-white">
              Overall Improvement Suggestions
            </h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {getAiRecommendations(averageContentScore).map((recommendation, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="p-4 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg"
              >
                <div className="flex items-start space-x-3">
                  <CheckCircle2 className="h-5 w-5 text-indigo-600 dark:text-indigo-400 mt-0.5" />
                  <p className="text-sm text-indigo-900 dark:text-indigo-100">{recommendation}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Platform-specific Recommendations */}
        <div className="space-y-6">
          {platforms.filter(p => p.enabled).map((platform, platformIndex) => {
            const Icon = platform.icon;
            const recommendations = getPlatformRecommendations(platform);
            const needsImprovement = platform.metrics.contentScore < 80;

            return (
              <div key={platform.id} className="border-t border-gray-100 dark:border-dark-700 pt-6">
                <div className="flex items-center space-x-2 mb-4">
                  <Icon className="h-5 w-5" style={{ color: platform.color }} />
                  <h3 className="text-md font-medium text-gray-900 dark:text-white">
                    {platform.name} Optimization Tips
                  </h3>
                  {needsImprovement && (
                    <AlertTriangle className="h-4 w-4 text-amber-500" />
                  )}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {recommendations.map((tip, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: (platformIndex * 0.2) + (index * 0.1) }}
                      className={`p-4 rounded-lg ${
                        needsImprovement
                          ? 'bg-amber-50 dark:bg-amber-900/20'
                          : 'bg-green-50 dark:bg-green-900/20'
                      }`}
                    >
                      <p className={`text-sm ${
                        needsImprovement
                          ? 'text-amber-900 dark:text-amber-100'
                          : 'text-green-900 dark:text-green-100'
                      }`}>
                        {tip}
                      </p>
                    </motion.div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </motion.div>
  );
};

// Platform Block Component
interface PlatformBlockProps {
  platform: Platform;
  isDragging: boolean;
  isBeingDragged: boolean;
  onDragStart: () => void;
  onDragEnd: () => void;
  onDrop: () => void;
  getScoreColor: (score: number) => string;
}

const PlatformBlock: React.FC<PlatformBlockProps> = ({
  platform,
  isDragging,
  isBeingDragged,
  onDragStart,
  onDragEnd,
  onDrop,
  getScoreColor
}) => {
  const Icon = platform.icon;
  
  return (
    <motion.div
      className={`bg-white dark:bg-dark-800 rounded-xl shadow-sm border border-gray-100 dark:border-dark-700 overflow-hidden transition-all ${
        isBeingDragged ? 'ring-2 ring-indigo-500 dark:ring-indigo-400 shadow-lg z-10 opacity-90' : ''
      } ${isDragging && !isBeingDragged ? 'border-dashed border-2 border-indigo-300 dark:border-indigo-700' : ''}`}
      initial={{ opacity: 0, y: 20 }}
      animate={{ 
        opacity: 1, 
        y: 0,
        scale: isBeingDragged ? 1.02 : 1
      }}
      whileHover={{ y: -5  }}
      draggable={true}
      onDragStart={(e) => {
        e.dataTransfer.setData('text/plain', platform.id);
        onDragStart();
      }}
      onDragEnd={onDragEnd}
      onDragOver={(e) => {
        e.preventDefault();
        (e.currentTarget as HTMLElement).classList.add('drag-over');
      }}
      onDragLeave={(e) => {
        e.preventDefault();
        (e.currentTarget as HTMLElement).classList.remove('drag-over');
      }}
      onDrop={(e) => {
        e.preventDefault();
        (e.currentTarget as HTMLElement).classList.remove('drag-over');
        onDrop();
      }}
    >
      <div className="p-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            <Icon className="h-5 w-5" style={{ color: platform.color }} />
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">{platform.name}</h3>
          </div>
          <div className={`text-sm font-medium ${getScoreColor(platform.metrics.contentScore)}`}>
            {platform.metrics.contentScore}%
          </div>
        </div>
        <div className="space-y-2">
          <div className="flex justify-between items-center">
            <span className="text-xs text-gray-500 dark:text-gray-400">Views</span>
            <span className="text-sm font-medium text-gray-900 dark:text-white">
              {platform.metrics.views.toLocaleString()}
            </span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-xs text-gray-500 dark:text-gray-400">Engagement</span>
            <span className="text-sm font-medium text-gray-900 dark:text-white">
              {platform.metrics.engagement}%
            </span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-xs text-gray-500 dark:text-gray-400">Followers</span>
            <span className="text-sm font-medium text-gray-900 dark:text-white">
              {platform.metrics.followers.toLocaleString()}
            </span>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default Performance;